import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  Star, 
  Clock, 
  User, 
  Calendar,
  Tag,
  FileText,
  Play,
  BookOpen,
  Shield,
  Code,
  Globe,
  Server,
  Smartphone,
  Users,
  Award,
  TrendingUp,
  Lightbulb,
  Palette,
  Building,
  FileText as FileTextIcon,
  Wrench,
  Target,
  Play as PlayIcon,
  BarChart3,
  Database,
  Zap,
  ExternalLink,
  File,
  Image,
  Video,
  Archive
} from 'lucide-react';

const ResourceDetail = ({ resource, onClose }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  // Función para obtener el ícono por nombre
  const getIconByName = (iconName) => {
    const iconMap = {
      'Code': Code,
      'Server': Server,
      'Globe': Globe,
      'Shield': Shield,
      'Users': Users,
      'BarChart3': BarChart3,
      'BookOpen': BookOpen,
      'Play': PlayIcon,
      'Target': Target,
      'Award': Award,
      'Smartphone': Smartphone,
      'Building': Building,
      'Palette': Palette,
      'Lightbulb': Lightbulb,
      'FileText': FileTextIcon,
      'Database': Database,
      'Zap': Zap,
      'Wrench': Wrench
    };
    return iconMap[iconName] || FileText;
  };

  // Función para obtener el ícono por tipo
  const getTypeIcon = (type) => {
    const typeMap = {
      'Tutorial': Play,
      'Guía': BookOpen,
      'Documentación': FileText,
      'Plantillas': FileTextIcon,
      'Estándares': Shield,
      'Comparación': BarChart3,
      'Ejemplos': Code,
      'Reporte': TrendingUp,
      'Casos de Estudio': Lightbulb
    };
    return typeMap[type] || FileText;
  };

  // Función para obtener el ícono del tipo de archivo
  const getFileIcon = (fileType) => {
    if (fileType.includes('image')) return Image;
    if (fileType.includes('video')) return Video;
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('tar')) return Archive;
    return File;
  };

  const handleDownload = (attachment) => {
    setIsDownloading(true);
    // Simular descarga
    setTimeout(() => {
      setIsDownloading(false);
      // Aquí se podría implementar la descarga real
      alert(`Archivo ${attachment.name} descargado exitosamente`);
    }, 2000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full h-full overflow-y-auto custom-scrollbar"
    >
      {/* Header del recurso */}
      <div className="bg-surface border border-gray-700 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              {(() => {
                const IconComponent = getTypeIcon(resource.type);
                return <IconComponent className="w-6 h-6 text-white" />;
              })()}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text mb-2">{resource.title}</h2>
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{resource.estimatedTime}</span>
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded-full">
                  {resource.difficulty}
                </span>
                <span className="px-2 py-1 bg-primary/20 text-primary rounded-full">
                  {resource.type}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="space-y-6">
        {/* Descripción */}
        <div className="bg-surface border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text mb-3">Descripción</h3>
          <p className="text-text-secondary leading-relaxed text-lg">{resource.description}</p>
        </div>

        {/* Metadatos */}
        <div className="bg-surface border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text mb-4">Información del Recurso</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-text-secondary" />
                <span className="text-text-secondary">Autor: <span className="text-text">{resource.author}</span></span>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-text-secondary" />
                <span className="text-text-secondary">Actualizado: <span className="text-text">{formatDate(resource.lastUpdated)}</span></span>
              </div>
              <div className="flex items-center space-x-3">
                <Download className="w-5 h-5 text-text-secondary" />
                <span className="text-text-secondary">Descargas: <span className="text-text">{resource.downloads}</span></span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <span className="text-text-secondary">Rating: <span className="text-text">{resource.rating}/5.0</span></span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-text-secondary" />
                <span className="text-text-secondary">Tiempo estimado: <span className="text-text">{resource.estimatedTime}</span></span>
              </div>
              <div className="flex items-center space-x-3">
                <Tag className="w-5 h-5 text-text-secondary" />
                <span className="text-text-secondary">Dificultad: <span className="text-text">{resource.difficulty}</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="bg-surface border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {resource.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Contenido del recurso */}
        <div className="bg-surface border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text mb-4">Contenido del Recurso</h3>
          <div className="bg-gray-800/50 rounded-lg p-6">
            <div className="space-y-4">
              {resource.content.sections.map((section, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1">
                    <h4 className="text-text font-medium mb-2">{section.title || `Sección ${index + 1}`}</h4>
                    {section.content && (
                      <p className="text-text-secondary leading-relaxed">{section.content}</p>
                    )}
                    {section.subsections && (
                      <div className="mt-3 ml-4 space-y-2">
                        {section.subsections.map((sub, subIndex) => (
                          <div key={subIndex} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-text-secondary text-sm">{sub}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Adjuntos (si existen) */}
        {resource.attachments && resource.attachments.length > 0 && (
          <div className="bg-surface border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-text mb-4">Archivos Adjuntos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {resource.attachments.map((attachment, index) => {
                const FileIcon = getFileIcon(attachment.type);
                return (
                  <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <FileIcon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-text font-medium truncate">{attachment.name}</h4>
                        <p className="text-text-secondary text-sm">{formatFileSize(attachment.size)}</p>
                        <p className="text-text-secondary text-xs">{attachment.type}</p>
                      </div>
                      <button
                        onClick={() => handleDownload(attachment)}
                        disabled={isDownloading}
                        className="btn btn-primary px-3 py-2 text-sm"
                      >
                        {isDownloading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Enlaces externos (si existen) */}
        {resource.externalLinks && resource.externalLinks.length > 0 && (
          <div className="bg-surface border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-text mb-4">Enlaces Relacionados</h3>
            <div className="space-y-3">
              {resource.externalLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-primary/50 transition-colors group"
                >
                  <ExternalLink className="w-5 h-5 text-primary group-hover:text-accent transition-colors" />
                  <div className="flex-1">
                    <h4 className="text-text font-medium group-hover:text-primary transition-colors">{link.title}</h4>
                    <p className="text-text-secondary text-sm">{link.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Información adicional */}
        <div className="bg-surface border border-gray-700 rounded-lg p-6">
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Acceso libre para empleados de Innova Developers
            </p>
            {resource.attachments && resource.attachments.length > 0 && (
              <p className="text-xs text-text-secondary mt-2">
                Haz clic en los archivos adjuntos para descargarlos
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ResourceDetail;
